import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useLogin } from '@cnx-saas/react-auth';
import { DexieIndexedDb } from '@cnx-saas/indexed-db';
import { confirmUserRegistration } from '../../../api/confirmRegistration';
import { isAxiosError } from '../../../utils/axios/axios.utils';
import { useQueryParams } from '../../../utils/hooks/useQuery';
import { availableRoutes } from '../../routes';
import { SplitView } from '../../split-view';
import { ErrorMessage, FormContainer } from './login.style';

function Login(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useLocation().state as { referrer?: Location } | undefined;
  const queryParams = useQueryParams<{ optinToken?: string; token?: string }>();
  const redirectUrl = state?.referrer
    ? `${state.referrer.pathname}${state.referrer.search}`
    : '/dashboard';

  const auth = useAuth();
  const login = useLogin();

  const [error, setError] = useState<string | undefined>(undefined);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (queryParams.optinToken) {
      confirmUserRegistration(queryParams.optinToken)
        .then(() => navigate('/'))
        .catch(() => {
          setError(t('login.error.emailConfirmationUnsuccessful'));
        });
    } else if (queryParams.token) {
      throw new Error('Functionality not implemented');
    } else if (auth.initialized && auth.authenticated) {
      navigate(redirectUrl);
    }
  }, [
    queryParams.optinToken,
    queryParams.token,
    auth,
    navigate,
    redirectUrl,
    t,
  ]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      await login(username, password);
      await DexieIndexedDb.getInstance().clearPushEvents();

      // NOTE: Scenarios where different users log in to the same
      // browser could cause one user to see load filters from someone else.
      await DexieIndexedDb.getInstance().clearOutdatedTourPlanLoadFilters();

      navigate(redirectUrl, { replace: true });
    } catch (e) {
      if (isAxiosError<string>(e) && e.response?.data?.error) {
        setError(t(`login.error.${e.response.data.error}`));
      } else {
        setError(t(`login.error.unknown_error`));
      }
      window.console.error('Failed to login.', e);
    }
  };

  const handleForgotPasswordButton = () => {
    navigate(availableRoutes.forgotpassword);
  };

  return (
    <SplitView>
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          padding: 4,
          paddingTop: 0,
          borderRadius: 8,
        }}
      >
        <h2>{t('login.title')}</h2>
        <FormContainer onSubmit={handleSubmit}>
          <TextField
            type="text"
            name="username"
            label={t('login.email.label')}
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            type="password"
            name="password"
            label={t('login.password.label')}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Typography
            variant="body1"
            sx={{
              width: 'fit-content',
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
            }}
            onClick={handleForgotPasswordButton}
          >
            {t('login.forgotPassword')}
          </Typography>
          <Button color="primary" type="submit">
            {t('login.button.label')}
          </Button>
        </FormContainer>
      </Box>
    </SplitView>
  );
}

export { Login };
